import { ChangeEvent, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FormControl, FormControlLabel, FormLabel, OutlinedInput, Radio, RadioGroup, Select } from '@mui/material';
import { ArrowDropDown } from '@mui/icons-material';
import { FormState, useForm } from 'react-hook-form';
import { Text, TextBlock } from '../../Atoms/Text';
import { InventoryAsset, SupportCasesItem } from '../../../types';
import { fetchSharedAccountCustomizations } from '../../../store/SharedAccountCustomizations';
import { getSelectedAccountIds } from '../../../store/User';
import { emailRegex, formatDate } from '../../../utils/helpers';
import AdditionalRecipients from '../../Pages/UserSettings/AdditionalRecipients';
import { TextSpan } from '../../Atoms/Text';
import { SELECT_INPUT_STYLE } from '../../../utils/styleHelpers';
import { getCaseSubject, useSupportCases } from '../../../store/SupportCase';
import { Icon } from '../../Atoms/Icon';

export interface FormData {
  Status: string;
  AccountId: string;
  ContactId: string;
  isServiceDown: string;
  serviceSubject: string;
  Description: string;
  issueType: string;
  Reason: string;
  Priority: string;
  Subject: string;
  OrderProductId: string;
  AdditionalRecipients: string;
  supplierCaseNumber: string;
}

const validateDescription = (value: string) => {
  if (value == 'required') return 'Please enter a detailed description of your issue';
  if (value == 'maxLength') return 'Character count is limited to 2,000';
  return true;
};

const infoText = () => {
  return (
    <Text
      size="sm14"
      className="text-left bg-yellow flex items-center justify-center p-4"
      color="grey5">
      <Icon
        type="alert"
        className="p-3 mr-3"
      />
      You can upload relevant attachments after you have created your case.
    </Text>
  );
};

const addressSelect = (service: InventoryAsset) => {
  const value = service.addressInfo.number ? `${service.addressInfo.number} - ${service.address}` : service.address;
  return (
    <Select
      input={<OutlinedInput />}
      IconComponent={() => <ArrowDropDown style={{ display: 'none' }} />}
      native
      size="small"
      disabled>
      <option
        key={0}
        value={value}>
        {value.length > 38 ? `${value.substring(0, 38)}...` : value}
      </option>
    </Select>
  );
};

export interface CaseFormProps {
  service: InventoryAsset;
  onSubmit: (formData: FormData) => Promise<void> | void;
  onError?: () => void;
  onChange?: (formState: FormState<FormData>) => void;
  additionalRecipients: string[];
  setAdditionalRecipients: (newVal: string[]) => void;
  saveDefault: boolean;
  setSaveDefault: (event: ChangeEvent<HTMLInputElement>, checked: boolean) => void;
  onCloseForm: () => void;
}

export const CaseForm = ({
  service,
  onSubmit,
  onError,
  onChange,
  additionalRecipients,
  setAdditionalRecipients,
  saveDefault,
  setSaveDefault,
  onCloseForm
}: CaseFormProps) => {
  const [isServiceDown, setIsServiceDown] = useState<boolean | null>(null);
  const [serviceSubject, setServiceSubject] = useState<string>('');
  const { register, handleSubmit, formState, reset, setValue, trigger } = useForm<FormData>({ mode: 'onChange' });
  const [additionalRecipInput, setAdditionalRecipInput] = useState<string>();
  const { errors, isValid } = formState;
  const [cases, setCases] = useState<SupportCasesItem[] | null>(null);
  const [openGeneralCase, setOpenGeneralCase] = useState<SupportCasesItem | null>(null);
  const [openCustomerCareCase, setOpenCustomerCareCase] = useState<SupportCasesItem | null>(null);
  const { fetchCases } = useSupportCases();

  useEffect(() => {
    register('isServiceDown', {
      validate: (value) => {
        if (value !== 'true' && value !== 'false') return false;
        return !(value === 'true' && service.status === 'Disconnected');
      }
    });

    trigger('isServiceDown');
  }, [register, service.status]);

  useEffect(() => {
    register('serviceSubject', {
      validate: (value, formValues) => !(formValues.isServiceDown === 'false' && !value)
    });
  }, [register]);

  useEffect(() => {
    const caseStore = getCaseSubject().subscribe((caseData) => {
      if (caseData == null) {
        setOpenCustomerCareCase(null);
        setOpenGeneralCase(null);
        setCases([]);
      } else {
        const casesForAsset = caseData.filter((case_) => case_.Associated_Inventory__c === service.id);
        if (casesForAsset.length > 0) {
          setOpenCustomerCareCase(
            casesForAsset.find((case_) => case_.caseType === 'Customer Care' && case_.status !== 'Closed') ?? null
          );
          setOpenGeneralCase(
            casesForAsset.find((case_) => case_.caseType === 'General Support' && case_.status !== 'Closed') ?? null
          );
        }
        setCases(caseData);
      }
    });

    fetchCases();
    fetchSharedAccountCustomizations(getSelectedAccountIds());
    return () => {
      if (caseStore) caseStore?.unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (onChange) onChange(formState);
  }, [formState, isValid, onChange]);

  const handleResetFormState = () => {
    reset({}, { keepIsValid: false, keepErrors: true, keepDirty: true, keepValues: true });
    if (onError) onError();
  };

  const onAdditionalRecipientsDelete = (email: string) => () => {
    const update = [...additionalRecipients].filter((v) => v !== email);
    setAdditionalRecipients(update);
  };

  const handleAdd = () => {
    if (additionalRecipInput && emailRegex.test(additionalRecipInput)) {
      setAdditionalRecipients([...(additionalRecipients || []), additionalRecipInput]);
      setAdditionalRecipInput('');
    }
  };

  const handleServiceStatusChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIsServiceDown(event.target.value === 'true');
    setValue('isServiceDown', event.target.value, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true
    });
    trigger('serviceSubject');
  };

  const handleServiceSubjectChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setServiceSubject(event.target.value);
    setValue('serviceSubject', event.target.value, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true
    });
  };

  const serviceRadioGroup = () => {
    return (
      <FormControl sx={{ flexDirection: 'row', alignItems: 'center', marginLeft: 'auto', marginRight: 'auto' }}>
        <FormLabel id="service-radio-buttons-group">
          <TextSpan size="sm14">Is service out or impaired?</TextSpan>
        </FormLabel>
        <RadioGroup
          sx={{
            marginLeft: '.5rem'
          }}
          row
          aria-labelledby="service-radio-buttons-group"
          value={isServiceDown}
          onChange={handleServiceStatusChange}>
          <FormControlLabel
            value={true}
            control={<Radio />}
            label="Yes"
          />
          <FormControlLabel
            value={false}
            control={<Radio />}
            label="No"
          />
        </RadioGroup>
      </FormControl>
    );
  };

  const requestTypeRadioGroup = () => {
    return (
      <FormControl>
        <FormLabel id="request-radio-buttons-group">
          <TextSpan size="sm14">Request Type</TextSpan>
        </FormLabel>
        <RadioGroup
          sx={{
            marginLeft: '1rem'
          }}
          row
          aria-labelledby="request-radio-buttons-group"
          value={serviceSubject}
          onChange={handleServiceSubjectChange}>
          {service.status !== 'Disconnected' && (
            <FormControlLabel
              value="Disconnect This Service"
              control={<Radio />}
              label="Disconnect This Service"
            />
          )}
          <FormControlLabel
            value="Billing or Administrative Issues"
            control={<Radio />}
            label="Billing or Administrative Issues"
          />
          <FormControlLabel
            value="Other"
            control={<Radio />}
            label="Other (RFO/RCA, Supplier Inquiry, etc.)"
          />
        </RadioGroup>
      </FormControl>
    );
  };

  return (
    <>
      <form
        data-testid="case-creation-form"
        onSubmit={handleSubmit(onSubmit, handleResetFormState)}
        id="case-creation-form">
        <div className="flex my-2 w-[20rem] m-auto">{addressSelect(service)}</div>
        <div className="flex mt-2 w-[20rem] m-auto">{serviceRadioGroup()}</div>
        {isServiceDown === null &&
          service.status !== 'Disconnected' &&
          (service.origin || service.upstack_support_managed) && (
            <TextSpan
              className="py-2 text-[12px] block bg-grey-1 rounded-md px-4"
              size="sm"
              weight="normal"
              key="alert-notice">
              Additional supplier authorization may be required.
            </TextSpan>
          )}
        {isServiceDown === true && cases !== null && openGeneralCase !== null && (
          <TextSpan
            className="py-2 text-[12px] block bg-grey-1 rounded-md px-4"
            size="sm"
            weight="normal"
            key="alert-notice">
            Please note, there is already a case opened on this service. Please reference case&nbsp;
            <Link
              className="text-indigo"
              title={`#${openGeneralCase.caseNumber}`}
              onClick={onCloseForm}
              to={`/support-cases?detailId=${openGeneralCase.id}`}>
              {`#${openGeneralCase.caseNumber}`}
            </Link>
            .
            <br />
            <br />
            If you have any questions surrounding the case on this service, please reach out to your UPSTACK team or
            email&nbsp;
            <a
              className="underline text-indigo"
              href="mailto:support@upstack.com">
              support@upstack.com
            </a>
          </TextSpan>
        )}
        {isServiceDown === false && cases !== null && openCustomerCareCase !== null && (
          <TextSpan
            className="py-2 text-[12px] block bg-grey-1 rounded-md px-4"
            size="sm"
            weight="normal"
            color="grey8"
            key="alert-notice">
            Please note, there is already a case opened on this service. Please reference case&nbsp;
            <Link
              className="text-indigo"
              title={`#${openCustomerCareCase.caseNumber}`}
              onClick={onCloseForm}
              to={`/support-cases?detailId=${openCustomerCareCase.id}`}>
              {`#${openCustomerCareCase.caseNumber}`}
            </Link>
            .
            <br />
            <br />
            If you have any questions surrounding the case on this service, please reach out to your UPSTACK team or
            email&nbsp;
            <a
              className="underline text-indigo"
              href="mailto:care@upstack.com">
              care@upstack.com
            </a>
          </TextSpan>
        )}
        {isServiceDown === false && cases !== null && openCustomerCareCase === null && (
          <div className="flex my-2 w-[19rem] m-auto">{requestTypeRadioGroup()}</div>
        )}
        {service.status === 'Disconnected' && (
          <div className="p-4 mt-2 bg-[#FFF4BB] flex flex-col gap-2 justify-center">
            <TextBlock
              size="sm"
              weight="normal"
              key="alert-notice">
              Note: This service has been
              <TextSpan
                size="sm"
                weight="semibold">
                &nbsp;Disconnected&nbsp;
              </TextSpan>
              {service.disconnectedDate ? `on ${formatDate(service.disconnectedDate)}` : ''}
              {service.disconnectedReason && (
                <>
                  &nbsp;due to&nbsp;
                  <TextSpan
                    size="sm"
                    weight="semibold">
                    {service.disconnectedReason}
                  </TextSpan>
                </>
              )}
              .
            </TextBlock>
            <TextBlock
              size="sm"
              weight="normal">
              Please contact support@upstack.com for additional questions.
            </TextBlock>
          </div>
        )}
        {((isServiceDown === false && cases !== null && openCustomerCareCase === null && serviceSubject !== '') ||
          (isServiceDown === true &&
            cases !== null &&
            openGeneralCase === null &&
            service.status !== 'Disconnected')) && (
          // eslint-disable-next-line react/jsx-indent
          <>
            {isServiceDown && (
              <div className="relative after:content-['Optional'] after:absolute after:right-12 after:top-2.5 after:text-gray-400 after:text-sm">
                <input
                  type="text"
                  className={`${SELECT_INPUT_STYLE}`}
                  id="supplierCaseNumber"
                  data-cy="issue-supplier-case-number"
                  aria-label="Supplier Case Number"
                  placeholder="Supplier Case #"
                  {...register('supplierCaseNumber', { required: false })}
                />
              </div>
            )}
            <textarea
              className={`${SELECT_INPUT_STYLE} ${errors.Description && 'border border-[red]'}`}
              data-cy="issue-details"
              id="description"
              {...register('Description', {
                required: true,
                maxLength: 2000,
                validate: validateDescription
              })}
              rows={5}
              placeholder="Details"
            />
            {errors.Description && (
              <div className="invalid-feedback">{validateDescription(errors.Description.type)}</div>
            )}
            <AdditionalRecipients
              additionalRecipients={additionalRecipients}
              handleAdd={handleAdd}
              additionalRecipInput={additionalRecipInput}
              setAdditionalRecipInput={setAdditionalRecipInput}
              saveDefault={saveDefault}
              setSaveDefault={setSaveDefault}
              onAdditionalRecipientsDelete={onAdditionalRecipientsDelete}
              showDefaultBox={true}
              styles={{
                divClassName: 'space-y-3 text-grey-5',
                textField: {
                  width: '20rem',
                  margin: 'auto',
                  display: 'flex'
                },
                box: {
                  '& > :not(:last-child)': { marginRight: 1 },
                  '& > *': { marginBottom: 1 },
                  width: '20.5rem',
                  marginLeft: 'auto',
                  maxHeight: '8rem',
                  overflow: 'auto'
                },
                chip: {
                  marginBottom: '.5rem',
                  borderRadius: '5px'
                },
                formControlLabel: {
                  display: 'flex',
                  margin: 'auto',
                  width: '20rem',
                  fontWeight: 'normal',
                  marginLeft: '-10px'
                }
              }}
            />
            {infoText()}
          </>
        )}
      </form>
    </>
  );
};
